// Centralized content for the Dalytix professional website.

export const stats = {
  global: [
    { value: '12+', label: 'representative enterprise use cases' },
    { value: '6', label: 'core AI & data practices' },
    { value: '3', label: 'delivery regions supported' },
    { value: '100%', label: 'senior-led delivery model' },
    { value: '24', label: 'production readiness checks' },
    { value: '1', label: 'accountable outcome owner' },
  ],
  practice: [
    { value: '30-60%', label: 'typical modernization target range' },
    { value: '12 wks', label: 'prototype-to-production planning horizon' },
    { value: '6', label: 'delivery stages from discovery to handover' },
    { value: '100%', label: 'governance designed in from day one' },
  ],
};

export const partners = [
  'Microsoft Azure',
  'Amazon Web Services',
  'Google Cloud',
  'Databricks',
  'Snowflake',
  'NVIDIA',
  'OpenAI',
  'Anthropic',
  'Confluent',
  'dbt Labs',
  'MongoDB',
  'Tecton',
];

export const technologyStack = [
  { name: 'Microsoft Azure', short: 'Azure', category: 'Cloud', tone: 'blue' },
  { name: 'Amazon Web Services', short: 'AWS', category: 'Cloud', tone: 'orange' },
  { name: 'Google Cloud', short: 'GCP', category: 'Cloud', tone: 'multi' },
  { name: 'Databricks', short: 'DB', category: 'Lakehouse', tone: 'red' },
  { name: 'Snowflake', short: 'SF', category: 'Warehouse', tone: 'cyan' },
  { name: 'dbt Labs', short: 'dbt', category: 'Transformation', tone: 'orange' },
  { name: 'OpenAI', short: 'AI', category: 'GenAI', tone: 'dark' },
  { name: 'Anthropic', short: 'A', category: 'GenAI', tone: 'warm' },
  { name: 'NVIDIA', short: 'NV', category: 'Acceleration', tone: 'green' },
  { name: 'Confluent', short: 'CF', category: 'Streaming', tone: 'indigo' },
  { name: 'MongoDB', short: 'MDB', category: 'Operational Data', tone: 'green' },
  { name: 'Tecton', short: 'TX', category: 'Feature Platform', tone: 'purple' },
];

export const technologyGroups = [
  {
    category: 'Cloud',
    title: 'Cloud foundations',
    text: 'Landing zones, secure data estates, cost controls, and production deployment patterns.',
    platforms: ['Microsoft Azure', 'Amazon Web Services', 'Google Cloud'],
  },
  {
    category: 'Data Platform',
    title: 'Lakehouse & warehouse',
    text: 'Reliable pipelines, semantic layers, governance controls, and analytics-ready data products.',
    platforms: ['Databricks', 'Snowflake', 'dbt Labs'],
  },
  {
    category: 'AI & Models',
    title: 'GenAI and model operations',
    text: 'Grounded copilots, RAG systems, evaluation harnesses, model serving, and GPU-aware design.',
    platforms: ['OpenAI', 'Anthropic', 'NVIDIA'],
  },
  {
    category: 'Streaming & Ops',
    title: 'Realtime and operational data',
    text: 'Event streaming, operational stores, feature platforms, observability, and release discipline.',
    platforms: ['Confluent', 'MongoDB', 'Tecton'],
  },
];

export const clients = [
  'Retail banking fraud program',
  'Healthcare operations analytics',
  'Retail personalization platform',
  'Energy forecasting initiative',
  'Insurance claims modernization',
  'Clinical data platform program',
  'Manufacturing reliability analytics',
  'Logistics visibility platform',
  'Telecom network intelligence',
  'Public-sector data trust program',
  'SaaS product analytics program',
  'Private-market research copilot',
];

export const clientEnvironments = [
  { name: 'Retail banking fraud program', category: 'Financial Services', icon: '/images/bank.svg', signal: 'Real-time decisions' },
  { name: 'Healthcare operations analytics', category: 'Healthcare', icon: '/images/healthcare.svg', signal: 'Governed clinical data' },
  { name: 'Retail personalization platform', category: 'Retail', icon: '/images/retail.svg', signal: 'Customer 360 activation' },
  { name: 'Manufacturing reliability analytics', category: 'Industrial', icon: '/images/manufacturing.svg', signal: 'Sensor and OEE data' },
  { name: 'Energy forecasting initiative', category: 'Energy', icon: '/images/case-energy.svg', signal: 'Grid and asset intelligence' },
  { name: 'Insurance claims modernization', category: 'Insurance', icon: '/images/case-insurance.svg', signal: 'Document AI workflow' },
  { name: 'Logistics visibility platform', category: 'Logistics', icon: '/images/case-logistics.svg', signal: 'Streaming ETA signals' },
  { name: 'SaaS product analytics program', category: 'SaaS', icon: '/images/case-saas.svg', signal: 'Product and revenue signals' },
];

export const services = [
  {
    id: 'ai-strategy',
    title: 'AI Strategy & Operating Model',
    short: 'Define business cases, governance, target architecture, and a CFO-ready AI roadmap.',
    description:
      'We help leadership teams translate AI ambition into a sequenced, measurable operating model — covering portfolio prioritization, governance, talent, platform, and risk.',
    capabilities: [
      'AI opportunity assessment and value mapping',
      'Operating model and capability design',
      'AI governance, risk and policy frameworks',
      'Executive education and change enablement',
      'Vendor and platform selection',
      'Multi-year AI investment roadmap',
    ],
    deliverables: [
      'Board-ready AI strategy deck',
      'Use-case portfolio with ROI estimates',
      'Target operating model blueprint',
      'AI governance and policy pack',
    ],
    icon: 'AI',
  },
  {
    id: 'generative-ai',
    title: 'Generative AI & Agents',
    short: 'Secure copilots, RAG platforms, and agentic workflows that put GenAI into the hands of every employee.',
    description:
      'From enterprise copilots to autonomous agents, we design grounded, evaluated, governed GenAI systems built on your data — with the guardrails enterprise risk teams expect.',
    capabilities: [
      'Enterprise copilots and assistants',
      'Retrieval-Augmented Generation platforms',
      'Multi-agent orchestration and tool use',
      'Model evaluation and red-teaming',
      'Prompt and policy management',
      'Token, cost and latency optimization',
    ],
    deliverables: [
      'Reference architecture (RAG / agents)',
      'Production copilot in 12 weeks',
      'Eval harness with regression tests',
      'GenAI risk and policy framework',
    ],
    icon: 'GA',
  },
  {
    id: 'data-engineering',
    title: 'Data Engineering & Lakehouse',
    short: 'Reliable pipelines, lakehouses, streaming systems, and governed data products that production teams trust.',
    description:
      'We build enterprise-grade data platforms — batch, streaming, real-time — using Databricks, Snowflake, Iceberg, dbt and Airflow, with quality, lineage and governance baked in.',
    capabilities: [
      'Lakehouse and data warehouse architecture',
      'Streaming and event-driven pipelines',
      'ELT, dbt and data product engineering',
      'Data quality, contracts, and lineage',
      'Migration from legacy ETL stacks',
      'FinOps and platform cost optimization',
    ],
    deliverables: [
      'Lakehouse foundation (Bronze/Silver/Gold)',
      'Domain-aligned data products',
      'Streaming reference implementation',
      'Data quality and lineage controls',
    ],
    icon: 'DE',
  },
  {
    id: 'mlops',
    title: 'MLOps & Production AI',
    short: 'Move models from notebooks to monitored, governed, scalable production systems on any cloud.',
    description:
      'We harden ML and GenAI systems for production: feature stores, CI/CD, monitoring, drift detection, governance, and on-call playbooks. SREs welcome.',
    capabilities: [
      'CI/CD for models and prompts',
      'Feature stores and serving infrastructure',
      'Model monitoring and drift detection',
      'Experiment tracking and registries',
      'Edge and real-time inference',
      'On-call playbooks and SLOs',
    ],
    deliverables: [
      'MLOps reference architecture',
      'Model registry and CI/CD pipelines',
      'Monitoring and drift dashboards',
      'Production-ready release process',
    ],
    icon: 'ML',
  },
  {
    id: 'analytics-bi',
    title: 'Analytics, BI & Decision Intelligence',
    short: 'Executive dashboards, forecasting, and embedded analytics that move from reports to decisions.',
    description:
      'We design semantic layers, self-serve BI, and forecasting capabilities your business actually adopts — connected to a single trusted set of metrics.',
    capabilities: [
      'Semantic layers and metric stores',
      'Executive dashboards and KPI trees',
      'Forecasting and demand planning',
      'Customer 360 and segmentation',
      'Embedded and product analytics',
      'Data storytelling and enablement',
    ],
    deliverables: [
      'KPI tree and metric catalog',
      'Executive analytics suite',
      'Forecasting and planning models',
      'Self-serve adoption program',
    ],
    icon: 'BI',
  },
  {
    id: 'data-governance',
    title: 'Data Governance & Trust',
    short: 'Lineage, quality, access, stewardship, and compliance for data leaders who carry real accountability.',
    description:
      'We build pragmatic governance that scales: data products, contracts, ownership, observability, access control, and audit-grade lineage across the modern stack.',
    capabilities: [
      'Data product catalogs and contracts',
      'Lineage and observability',
      'Privacy, masking and access control',
      'Quality SLAs and stewardship',
      'Regulatory readiness (GDPR, HIPAA, SOC 2)',
      'Federated governance operating model',
    ],
    deliverables: [
      'Governance operating model',
      'Lineage + quality control plane',
      'Privacy & access reference design',
      'Audit-ready compliance evidence',
    ],
    icon: 'DG',
  },
  {
    id: 'cloud-modernization',
    title: 'Cloud Data Modernization',
    short: 'Migrate, refactor and modernize legacy data estates — Hadoop, Teradata, Oracle, SSIS — to a cloud-native lakehouse.',
    description:
      'We retire legacy data debt, rationalize platforms and deliver cloud-native foundations — usually with a 30–60% TCO improvement and far better developer velocity.',
    capabilities: [
      'Legacy ETL and warehouse migration',
      'Hadoop / Teradata / Oracle modernization',
      'Cloud landing zone for data',
      'Pattern-based refactor accelerators',
      'Parallel run and cutover',
      'Decommissioning and FinOps',
    ],
    deliverables: [
      'Migration discovery and wave plan',
      'Refactored, tested workloads',
      'Cutover and rollback runbooks',
      'TCO and benefits realization model',
    ],
    icon: 'CM',
  },
  {
    id: 'computer-vision',
    title: 'Computer Vision & Edge AI',
    short: 'Vision, IoT, and edge AI systems for industrial, retail, healthcare and physical-operations use cases.',
    description:
      'From safety analytics to defect detection, we deliver real-time vision and edge AI systems that survive the realities of warehouses, factory floors and clinical environments.',
    capabilities: [
      'Defect and quality detection',
      'Worker safety and PPE analytics',
      'Shelf, store, and yard intelligence',
      'Medical imaging support',
      'Edge inference (NVIDIA Jetson, ONNX)',
      'Annotation and active learning',
    ],
    deliverables: [
      'Vision pipeline and model suite',
      'Edge deployment runbook',
      'Annotation and labeling ops',
      'Real-time analytics dashboards',
    ],
    icon: 'CV',
  },
  {
    id: 'ai-security',
    title: 'AI Security & Responsible AI',
    short: 'Threat modeling, red-teaming, evals and policy guardrails for AI systems exposed to customers.',
    description:
      'We harden AI systems against prompt injection, exfiltration and abuse — and document the controls your CISO, regulator and board need to see.',
    capabilities: [
      'AI threat modeling and red-teaming',
      'Prompt injection and jailbreak defense',
      'PII and policy guardrails',
      'Bias and fairness evaluation',
      'Model and prompt observability',
      'Regulatory mapping (EU AI Act, NIST RMF)',
    ],
    deliverables: [
      'AI risk and threat model',
      'Red-team report with findings',
      'Guardrail and eval pipeline',
      'Responsible AI policy pack',
    ],
    icon: 'AS',
  },
];

export const industries = [
  {
    id: 'financial-services',
    name: 'Financial Services',
    summary:
      'Risk, fraud, compliance and customer 360 use cases for retail, commercial and capital markets institutions.',
    challenges: [
      'Real-time fraud and AML detection',
      'Customer 360 and personalization',
      'Regulatory reporting automation',
      'Credit and behavioral risk modeling',
    ],
    outcomes: [
      '45% lift in fraud precision',
      '$120M annual losses avoided',
      '30% faster regulatory close',
      '18% cross-sell win rate uplift',
    ],
    image: '/images/case-bank.svg',
  },
  {
    id: 'healthcare',
    name: 'Healthcare & Life Sciences',
    summary:
      'Clinical operations, population health, medical imaging, and pharma R&D analytics with HIPAA-grade governance.',
    challenges: [
      'Care coordination and patient flow',
      'Clinical trial analytics',
      'Imaging and diagnostic support',
      'Population health insights',
    ],
    outcomes: [
      '30% reduction in patient wait time',
      '6 weeks earlier trial data lock',
      '22% improvement in readmission flags',
      'HIPAA-aligned data foundation',
    ],
    image: '/images/case-healthcare.svg',
  },
  {
    id: 'retail-cpg',
    name: 'Retail & Consumer Goods',
    summary:
      'Personalization, demand forecasting, supply chain optimization, and unified commerce analytics.',
    challenges: [
      'Personalization and recommendations',
      'Demand and inventory forecasting',
      'Pricing and promotion optimization',
      'Unified omnichannel analytics',
    ],
    outcomes: [
      '+47% basket conversion',
      '−18% inventory carrying cost',
      '4x faster promotion analysis',
      '+9 NPS uplift on personalization',
    ],
    image: '/images/case-retail.svg',
  },
  {
    id: 'manufacturing',
    name: 'Manufacturing & Industrial',
    summary:
      'Predictive maintenance, quality, OEE, energy, and connected-factory analytics for industrial operations.',
    challenges: [
      'Predictive maintenance and downtime',
      'Quality and defect detection',
      'Energy and OEE optimization',
      'Connected factory data foundations',
    ],
    outcomes: [
      '−41% unplanned downtime',
      '−18% energy waste',
      '+12% OEE on critical lines',
      '1,240 sensors unified into one platform',
    ],
    image: '/images/case-manufacturing.svg',
  },
  {
    id: 'energy-utilities',
    name: 'Energy & Utilities',
    summary:
      'Grid optimization, asset reliability, ESG analytics and renewables forecasting for energy operators.',
    challenges: [
      'Grid load forecasting',
      'Asset reliability and outage prediction',
      'ESG and emissions analytics',
      'Renewables and storage optimization',
    ],
    outcomes: [
      '−18% energy waste',
      '−54% MTTR on critical assets',
      '38kt CO₂ avoided annually',
      '99.99% telemetry pipeline uptime',
    ],
    image: '/images/case-energy.svg',
  },
  {
    id: 'insurance',
    name: 'Insurance',
    summary:
      'Claims automation, underwriting AI, fraud, customer 360 and reinsurance analytics for P&C, life and health.',
    challenges: [
      'Claims triage and automation',
      'Underwriting and pricing intelligence',
      'Fraud and special investigations',
      'Customer 360 and retention',
    ],
    outcomes: [
      '3.2x faster claim cycle',
      '+22 NPS post implementation',
      '−61% false-positive fraud alerts',
      '24% combined ratio improvement',
    ],
    image: '/images/case-insurance.svg',
  },
  {
    id: 'public-sector',
    name: 'Public Sector',
    summary:
      'Cross-agency data sharing, citizen services, fraud prevention, and trustworthy AI for governments.',
    challenges: [
      'Cross-agency data sharing',
      'Benefits fraud detection',
      'Citizen services and case management',
      'Responsible and explainable AI',
    ],
    outcomes: [
      '12 agencies on one trusted graph',
      '$84M benefits fraud avoided',
      '40% faster case resolution',
      'Audit-grade explainability',
    ],
    image: '/images/case-public.svg',
  },
  {
    id: 'logistics',
    name: 'Transportation & Logistics',
    summary:
      'Supply-chain visibility, ETA prediction, route and yard optimization, carrier and fleet analytics.',
    challenges: [
      'End-to-end shipment visibility',
      'ETA and exception prediction',
      'Carrier scoring and capacity',
      'Yard and route optimization',
    ],
    outcomes: [
      '−22% on-time variance',
      '−14% cost per ton-mile',
      '312 carriers scored daily',
      '14.6M shipments orchestrated',
    ],
    image: '/images/case-logistics.svg',
  },
  {
    id: 'telecom',
    name: 'Telecommunications & Media',
    summary:
      'Network AI, customer experience, churn, content recommendation and ad-tech analytics for telecom and media.',
    challenges: [
      'Network anomaly detection',
      'Churn and CX analytics',
      'Content and ad personalization',
      'Field-ops optimization',
    ],
    outcomes: [
      '99.99% network uptime',
      '−54% MTTR on incidents',
      '+9.6% customer retention',
      '2,180 anomalies prevented daily',
    ],
    image: '/images/case-telecom.svg',
  },
  {
    id: 'saas-tech',
    name: 'SaaS & Technology',
    summary:
      'Product analytics, growth, churn prediction, billing intelligence, and AI-native product features.',
    challenges: [
      'Product engagement analytics',
      'Churn and expansion prediction',
      'AI-native feature delivery',
      'Billing and pricing intelligence',
    ],
    outcomes: [
      '−27% predicted churn saved',
      '+18% expansion ARR uplift',
      'Risk model AUC 0.92',
      'Self-serve analytics across 30 teams',
    ],
    image: '/images/case-saas.svg',
  },
  {
    id: 'pharma-life-sciences',
    name: 'Pharma & Biotech',
    summary:
      'Clinical analytics, drug discovery support, manufacturing quality, and commercial intelligence.',
    challenges: [
      'Clinical trial operations and analytics',
      'Drug discovery support',
      'Manufacturing quality and CoA',
      'Commercial and brand analytics',
    ],
    outcomes: [
      '−6 weeks to data lock',
      '34 sites & 12 countries unified',
      '18,200 patients on one cohort plane',
      'Auto-generated DSMB packs',
    ],
    image: '/images/case-pharma.svg',
  },
];

export const caseStudies = [
  {
    id: 'continental-trust-fraud',
    client: 'Anonymized European Retail Bank',
    industry: 'Financial Services',
    title: 'Real-time fraud detection at 2.4M events / minute',
    metric: '−45%',
    metricLabel: 'fraud losses, 12 months',
    duration: '14 weeks',
    region: 'EMEA',
    image: '/images/case-fraud.svg',
    summary:
      'An anonymized European retail bank replaced rules-based fraud detection with a streaming ML platform delivering real-time decisions across cards, payments and digital channels.',
    challenge:
      'The client operated five fragmented fraud engines built on a 12-year-old rules platform. False-positive rates above 12% drove customer friction, and emerging fraud vectors took weeks to model. Detection latency averaged 4.8 seconds — too slow for instant payments.',
    solution:
      'Dalytix delivered a unified streaming detection platform on Confluent and Databricks. We built a feature store with 1,200+ behavioral features, deployed gradient boosted and graph models, added an analyst workbench for case management, and rolled out a real-time decision API at 82ms p99.',
    approach: [
      'Discovery across 5 fraud engines, 9 payment rails, and 38 alert types',
      'Streaming feature store with sub-second freshness',
      'Hybrid graph + GBM models with case-level explanations',
      'Decisioning API with shadow, canary and full-rollout stages',
      'Analyst workbench, feedback loop and active learning',
      'Continuous monitoring, drift detection, and governance',
    ],
    outcomes: [
      { value: '−45%', label: 'fraud losses avoided' },
      { value: '−61%', label: 'reduction in false positives' },
      { value: '82ms', label: 'p99 inference latency' },
      { value: '€120M', label: 'annual losses prevented' },
    ],
    quote:
      '"Dalytix is the only partner that talked about decisioning latency and policy at the same depth as model accuracy. That balance is exactly why this works in production."',
    quoteAuthor: 'Group Head of Financial Crime',
  },
  {
    id: 'northwind-clinical-ai',
    client: 'Anonymized Integrated Health Network',
    industry: 'Healthcare',
    title: 'Clinical AI assistant across 84 hospitals',
    metric: '−30%',
    metricLabel: 'avg. patient wait time',
    duration: '20 weeks',
    region: 'North America',
    image: '/images/case-healthcare.svg',
    summary:
      'An anonymized integrated delivery network deployed a HIPAA-aligned clinical assistant grounded in EHR documents, reducing administrative burden and accelerating care coordination.',
    challenge:
      'Clinicians spent a significant share of their time on documentation and chart review. The CIO wanted to deploy generative AI safely after earlier pilots were blocked by data segregation, audit and PHI handling concerns.',
    solution:
      'Dalytix designed a HIPAA-aligned RAG architecture with PHI tokenization, role-based access, audit logging and grounded retrieval over 14M structured and unstructured documents. We deployed three workflows: handoff summarization, prior authorization drafting, and care-pathway recommendations.',
    approach: [
      'Privacy review and PHI handling architecture',
      'Embedding pipeline over 14M EHR documents',
      'Grounded RAG with citations and refusal policies',
      'Clinical eval suite reviewed by physician panel',
      'Pilot in 6 hospitals, scaled to 84',
      'On-call SRE and continuous evaluation',
    ],
    outcomes: [
      { value: '−30%', label: 'avg. patient wait time' },
      { value: '+22%', label: 'documentation time saved' },
      { value: '96.4%', label: 'eval faithfulness score' },
      { value: '84', label: 'hospitals live' },
    ],
    quote:
      '"This is not a chatbot — it is a piece of regulated clinical infrastructure. Dalytix earned that distinction with their evidence and engineering."',
    quoteAuthor: 'Chief Medical Information Officer',
  },
  {
    id: 'aurora-personalization',
    client: 'Anonymized Multi-Brand Retailer',
    industry: 'Retail & Consumer Goods',
    title: 'Personalization platform driving +47% conversion',
    metric: '+47%',
    metricLabel: 'basket conversion',
    duration: '16 weeks',
    region: 'Global',
    image: '/images/case-retail.svg',
    summary:
      'A multi-banner retail group consolidated five customer data systems into a unified personalization platform driving recommendations, journey orchestration and lifetime value.',
    challenge:
      'The retailer\'s five banners ran independent CRMs and recommenders. Marketing campaigns relied on weekly extracts. Personalization was inconsistent, attribution was guesswork, and the CMO had no shared view of customer LTV.',
    solution:
      'We delivered a real-time customer 360 on Snowflake, an event-driven activation layer, and a recommender ensemble (collaborative + content + sequence) deployed on Databricks. A/B framework, holdouts and incrementality measurement were built in from day one.',
    approach: [
      'Customer 360 across five banners',
      'Streaming events into Snowflake + Iceberg',
      'Recommendation ensemble (CF + content + sequence)',
      'Reverse-ETL activation into Braze and Salesforce',
      'A/B + incrementality framework',
      'CMO-level LTV and attribution dashboards',
    ],
    outcomes: [
      { value: '+47%', label: 'basket conversion lift' },
      { value: '+9 pts', label: 'NPS uplift' },
      { value: '$24.6M', label: 'attributed revenue lift' },
      { value: '5', label: 'banners on one platform' },
    ],
    quote:
      '"We replaced six years of stitched-together personalization logic with one platform. The first 90 days alone paid for the engagement."',
    quoteAuthor: 'Chief Digital Officer',
  },
  {
    id: 'atlas-predictive-maintenance',
    client: 'Anonymized Industrial Manufacturer',
    industry: 'Manufacturing & Industrial',
    title: 'Predictive maintenance across 3 plants',
    metric: '−41%',
    metricLabel: 'unplanned downtime',
    duration: '18 weeks',
    region: 'EMEA & North America',
    image: '/images/case-manufacturing.svg',
    summary:
      'An industrial manufacturer reduced unplanned downtime across three plants by unifying sensor data, building anomaly and remaining-useful-life models, and embedding predictions into the maintenance workflow.',
    challenge:
      'The client struggled with reactive maintenance: a meaningful share of plant capacity was lost to unplanned downtime. Multiple historians, OPC stacks, and SCADA flavors made data analysis a multi-week effort.',
    solution:
      'We deployed a Databricks lakehouse with edge ingestion, normalized 1,240 sensors, built anomaly and RUL models, and integrated predictions into the SAP PM workflow with field-tested mobile dispatch.',
    approach: [
      'Edge ingestion bridge across OPC-UA + MQTT',
      'Sensor normalization and engineering features',
      'Anomaly + RUL model ensemble',
      'SAP PM workflow integration',
      'Mobile-first technician experience',
      'Plant-by-plant rollout with KPI gating',
    ],
    outcomes: [
      { value: '−41%', label: 'unplanned downtime' },
      { value: '+12%', label: 'OEE on critical lines' },
      { value: '1,240', label: 'sensors unified' },
      { value: '$18.7M', label: 'annualized savings' },
    ],
    quote:
      '"This is the rare AI program plant managers ask for more of. The handoff to operations was the best part."',
    quoteAuthor: 'VP Manufacturing Excellence',
  },
  {
    id: 'kingsford-claims',
    client: 'Anonymized P&C Insurer',
    industry: 'Insurance',
    title: 'Straight-through claims automation',
    metric: '3.2x',
    metricLabel: 'faster claim cycle',
    duration: '22 weeks',
    region: 'North America',
    image: '/images/case-insurance.svg',
    summary:
      'An anonymized P&C insurer automated claims workflows using document AI, fraud scoring and policy reasoning, improving cycle time and customer experience.',
    challenge:
      'Manual claims handling drove cycle times of 12.4 days, customer dissatisfaction, and uneven leakage controls across 14 regional centers.',
    solution:
      'Dalytix designed a document-AI pipeline (FNOL + supporting docs), policy-aware reasoning agents, integrated fraud scoring, and an adjuster console with explanations and override paths.',
    approach: [
      'Document AI pipeline for FNOL + evidence',
      'Policy-aware reasoning agents',
      'Fraud and salvage scoring ensemble',
      'Adjuster console with explanations',
      'Reinsurance and audit trail integration',
      'Continuous policy and prompt governance',
    ],
    outcomes: [
      { value: '3.2x', label: 'faster claims cycle' },
      { value: '+22 pts', label: 'NPS uplift' },
      { value: '62%', label: 'claims fully automated' },
      { value: '$84M', label: 'leakage reduction' },
    ],
    quote:
      '"Customers feel the difference. So does our combined ratio."',
    quoteAuthor: 'EVP Claims',
  },
  {
    id: 'helios-grid',
    client: 'Anonymized Energy Operator',
    industry: 'Energy & Utilities',
    title: 'Grid optimization across 214 substations',
    metric: '−18%',
    metricLabel: 'energy waste',
    duration: '24 weeks',
    region: 'EMEA',
    image: '/images/case-energy.svg',
    summary:
      'An energy operator improved forecasting and reduced critical-asset response time with a unified grid analytics platform.',
    challenge:
      'Disparate SCADA, AMI and weather feeds prevented the client from forecasting load and proactively dispatching maintenance crews. Renewables curtailment was costly.',
    solution:
      'We delivered a real-time grid intelligence platform on Azure with hybrid forecasting models (gradient boosted + Bayesian), asset reliability scoring, and crew-routing optimization.',
    approach: [
      'Real-time SCADA + AMI integration',
      'Hybrid forecasting (GBM + Bayesian)',
      'Asset reliability and outage scoring',
      'Crew dispatch optimization',
      'ESG and emissions reporting',
      'Regulator-ready audit trail',
    ],
    outcomes: [
      { value: '−18%', label: 'energy waste' },
      { value: '−54%', label: 'critical-asset MTTR' },
      { value: '38kt', label: 'CO₂ avoided / year' },
      { value: '214', label: 'substations live' },
    ],
    quote: '"The forecasting is uncanny — and the regulatory pack writes itself."',
    quoteAuthor: 'Head of Grid Operations',
  },
  {
    id: 'lumen-saas-churn',
    client: 'Anonymized B2B SaaS Platform',
    industry: 'SaaS & Technology',
    title: 'Predictive churn & expansion playbooks',
    metric: '−27%',
    metricLabel: 'churn (saved by proactive ops)',
    duration: '10 weeks',
    region: 'North America',
    image: '/images/case-saas.svg',
    summary:
      'A B2B SaaS platform reduced preventable churn by combining product engagement, financial signals and CSM activity into a churn-and-expansion model with playbook-driven outreach.',
    challenge:
      'The CS team relied on lagging indicators. Account scoring was inconsistent, and engagement data lived in five separate tools.',
    solution:
      'We built a unified product analytics platform, a churn-and-expansion ensemble with calibrated risk tiers, and integrated playbooks in Gainsight and Salesforce.',
    approach: [
      'Product event ingestion and modeling',
      'Churn + expansion ensemble (AUC 0.92)',
      'Calibrated risk tiers and playbooks',
      'CSM workflow in Gainsight + Salesforce',
      'Closed-loop measurement of saves',
      'Self-serve account 360 for sales',
    ],
    outcomes: [
      { value: '−27%', label: 'gross churn (saved)' },
      { value: '+18%', label: 'expansion ARR' },
      { value: '0.92', label: 'risk model AUC' },
      { value: '38%', label: 'at-risk accounts saved' },
    ],
    quote: '"The first quarter we trusted the score, retention jumped 14 points. We never went back."',
    quoteAuthor: 'VP Customer Success',
  },
  {
    id: 'meridian-supply-chain',
    client: 'Anonymized Logistics Network',
    industry: 'Transportation & Logistics',
    title: 'Supply-chain intelligence platform',
    metric: '−22%',
    metricLabel: 'on-time variance',
    duration: '20 weeks',
    region: 'Global',
    image: '/images/case-logistics.svg',
    summary:
      'A logistics network unified carrier and shipment data into one supply-chain intelligence platform, improving on-time performance and cost visibility.',
    challenge:
      'Carrier data, EDI 214 events and customer commitments lived in disconnected systems. ETA accuracy varied wildly. Exceptions were detected far too late to act on.',
    solution:
      'We delivered a streaming ETA model, exception detection, carrier scoring, and a customer-facing visibility portal — with FinOps tracking baked in.',
    approach: [
      'Streaming ingestion of EDI + telematics',
      'ETA + exception ensemble',
      'Carrier scoring and capacity planning',
      'Customer-facing visibility portal',
      'FinOps and cost-per-ton-mile telemetry',
      'API integration with TMS and ERP',
    ],
    outcomes: [
      { value: '−22%', label: 'on-time variance' },
      { value: '−14%', label: 'cost per ton-mile' },
      { value: '14.6M', label: 'shipments orchestrated' },
      { value: '312', label: 'carriers scored daily' },
    ],
    quote: '"For the first time we have a real-time view of our network — and our customers can feel it."',
    quoteAuthor: 'Chief Supply Chain Officer',
  },
  {
    id: 'beacon-public-sector',
    client: 'Anonymized Public Services Body',
    industry: 'Public Sector',
    title: 'Cross-agency data trust for 12 agencies',
    metric: '$84M',
    metricLabel: 'benefits fraud avoided',
    duration: '32 weeks',
    region: 'EMEA',
    image: '/images/case-public.svg',
    summary:
      'A public services body unified agencies on a trusted, auditable data graph, accelerating case resolution and improving fraud detection.',
    challenge:
      'Twelve agencies operated independent identity, benefits and case systems. Inter-agency requests took weeks. Fraud rings exploited the gaps.',
    solution:
      'We built a federated graph data platform with consent-based sharing, identity resolution, fraud detection and an analyst console — all auditable to regulator standards.',
    approach: [
      'Federated graph platform with consent rules',
      'Identity resolution across 12 agencies',
      'Behavioral fraud detection and review queue',
      'Analyst console with case explanations',
      'Audit-grade lineage and access logs',
      'Citizen privacy and DPIA framework',
    ],
    outcomes: [
      { value: '$84M', label: 'benefits fraud avoided' },
      { value: '12', label: 'agencies unified' },
      { value: '40%', label: 'faster case resolution' },
      { value: 'Audit-grade', label: 'lineage + access logs' },
    ],
    quote: '"Trust was the goal. The fraud reduction was the headline. The lineage was the moat."',
    quoteAuthor: 'Programme Director',
  },
  {
    id: 'polarpharma-trial-acceleration',
    client: 'Anonymized Pharma Sponsor',
    industry: 'Pharma & Biotech',
    title: 'Clinical trial analytics platform',
    metric: '−6 wks',
    metricLabel: 'to data lock',
    duration: '26 weeks',
    region: 'Global',
    image: '/images/case-pharma.svg',
    summary:
      'A pharma sponsor accelerated trial data lock across sites and regions with a unified clinical analytics platform and auto-generated DSMB packs.',
    challenge:
      'Each CRO and EDC vendor produced different schemas. DSMB packs took weeks to assemble. Trial operations had no single view of recruitment, deviations or quality.',
    solution:
      'We unified EDC, CTMS, lab and ePRO data into a SDTM-aligned analytics layer, built recruitment and deviation analytics, and auto-generated DSMB packs to a validated template.',
    approach: [
      'EDC + CTMS + lab + ePRO unification',
      'SDTM-aligned analytics layer',
      'Recruitment and deviation analytics',
      'Auto-generated DSMB packs',
      'Validated CSV documentation',
      'Site-level operations cockpit',
    ],
    outcomes: [
      { value: '−6 wks', label: 'to data lock' },
      { value: '34', label: 'sites unified' },
      { value: '12', label: 'countries' },
      { value: '18,200', label: 'patients in cohort plane' },
    ],
    quote: '"The DSMB pack used to take three weeks. It now takes ninety minutes — and is more defensible."',
    quoteAuthor: 'Director of Trial Operations',
  },
  {
    id: 'vector-network-ai',
    client: 'Anonymized Telecom Operator',
    industry: 'Telecommunications & Media',
    title: 'Network AI across a national tower estate',
    metric: '99.99%',
    metricLabel: 'network uptime',
    duration: '28 weeks',
    region: 'APAC',
    image: '/images/case-telecom.svg',
    summary:
      'A telecom operator deployed network anomaly detection and automated remediation across a large tower estate, improving uptime and reducing MTTR.',
    challenge:
      'The NOC was alarm-fatigued and reactive. Issues escalated faster than humans could correlate. Customer experience suffered before anyone in the NOC noticed.',
    solution:
      'We delivered a streaming anomaly platform with hierarchical correlation, customer-impact scoring, and automated playbooks — wrapped in a NOC console designed with lead operators.',
    approach: [
      'Streaming telemetry from a large tower estate',
      'Hierarchical anomaly + correlation models',
      'Customer-impact scoring',
      'Automated remediation playbooks',
      'NOC console designed with operators',
      'Continuous evaluation against incidents',
    ],
    outcomes: [
      { value: '99.99%', label: 'network uptime' },
      { value: '−54%', label: 'MTTR' },
      { value: 'Large', label: 'tower estate monitored' },
      { value: '2,180', label: 'anomalies / day prevented' },
    ],
    quote: '"NOC stress dropped overnight. The team can finally focus on engineering, not firefighting."',
    quoteAuthor: 'SVP Network Operations',
  },
  {
    id: 'stellar-enterprise-genai',
    client: 'Anonymized Asset Manager',
    industry: 'Financial Services',
    title: 'Enterprise GenAI copilot for analysts',
    metric: '−68%',
    metricLabel: 'analyst hours on research prep',
    duration: '12 weeks',
    region: 'North America',
    image: '/images/case-genai.svg',
    summary:
      'An asset manager deployed a grounded RAG copilot over policy and research documents, cutting research prep time with citation coverage and audit logs.',
    challenge:
      'Junior analysts spent 70% of their time gathering and reconciling research from internal and external sources. Compliance forbade unconstrained LLM tools.',
    solution:
      'Dalytix built a private, grounded RAG platform with hybrid retrieval (BM25 + dense), citation enforcement, document-level access control, and audit logging — all hosted in the client cloud.',
    approach: [
      'Hybrid retrieval over 4M documents',
      'Document-level access enforcement',
      'Citation-required generation',
      'Audit logging and chat retention controls',
      'Eval harness with expert review',
      'Compliance and red-team sign-off',
    ],
    outcomes: [
      { value: '−68%', label: 'analyst prep time' },
      { value: '4M', label: 'documents indexed' },
      { value: '96.4%', label: 'faithfulness' },
      { value: '1.2s', label: 'avg. response time' },
    ],
    quote: '"It is the first AI tool our compliance team enjoyed reviewing."',
    quoteAuthor: 'Chief Technology Officer',
  },
];

export const insights = [
  {
    id: 'pilot-to-production',
    category: 'Perspective',
    title: 'From pilot to production: 9 patterns that decide AI ROI',
    excerpt:
      'Across enterprise AI programs, the same nine patterns separate the work that ships from the pilots that quietly stall. Here is the playbook.',
    author: 'Dr. Anika Khoury',
    role: 'Global Head of AI Strategy',
    date: 'April 18, 2026',
    readTime: '8 min',
    image: '/images/insight-1.svg',
    tags: ['AI Strategy', 'Operating Model', 'GenAI'],
    body: [
      'The hardest part of enterprise AI is not the model — it is everything around the model. The patterns that separate production AI from indefinite pilots are organizational, architectural, and operational, not algorithmic.',
      'Pattern 1: A named accountable executive. Programs without one are political theater. Pattern 2: A single source of truth for the data the model consumes. Pattern 3: A path to production designed before the first notebook is opened.',
      'Pattern 4: An eval harness that runs on every change. Pattern 5: Cost, latency and privacy targets agreed before architecture is locked. Pattern 6: A sunsetting plan for the system being replaced. Pattern 7: An on-call rotation. Pattern 8: A regulator-ready paper trail. Pattern 9: A six-week feedback loop with the business.',
      'When all nine are present, AI programs ship. When two or more are missing, programs stall. The trick is to design the program around all nine from week one.',
    ],
  },
  {
    id: 'modern-lakehouse',
    category: 'Research',
    title: 'The modern lakehouse stack: a practitioner\'s reference 2026',
    excerpt:
      'Iceberg, Delta, and Hudi have converged. The interesting design decisions have moved up the stack. Our reference architecture for 2026.',
    author: 'Priyanka Rao',
    role: 'Partner, Data Engineering',
    date: 'April 09, 2026',
    readTime: '12 min',
    image: '/images/insight-2.svg',
    tags: ['Data Engineering', 'Lakehouse', 'Architecture'],
    body: [
      'Format wars are over. Iceberg, Delta and Hudi have all converged on the features practitioners need: ACID, time travel, schema evolution, hidden partitioning, and engine interoperability.',
      'The interesting questions have moved one layer up. How do you organize medallion zones across business units? How do you separate data products from raw zones? How do you do governance and lineage that engineers actually use?',
      'We are publishing our 2026 reference architecture: domain-aligned data products, contract-tested ELT, governance-as-code, FinOps from day one, and a single semantic layer that BI, products and AI can share.',
    ],
  },
  {
    id: 'mlops-regulated',
    category: 'Playbook',
    title: 'MLOps for regulated industries: the controls list',
    excerpt:
      'Banks, hospitals and public agencies need more than experiment trackers. The exact controls list to satisfy auditors without slowing engineering.',
    author: 'Samuel Hadid',
    role: 'Director of MLOps',
    date: 'March 28, 2026',
    readTime: '14 min',
    image: '/images/insight-3.svg',
    tags: ['MLOps', 'Compliance', 'Production AI'],
    body: [
      'Auditors do not care about your experiment tracker. They care about evidence. Specifically, evidence that you know what model is in production, who approved it, what data it was trained on, how it is monitored, and what happens when it drifts.',
      'The controls list has eleven items: change management, model registry, training data lineage, eval harness, approval gates, deployment evidence, monitoring, drift response, retraining policy, kill switches and audit retention.',
      'Built right, this list does not slow you down — it accelerates you. The teams that nail it ship faster, because there are no last-minute audit fire drills, ever.',
    ],
  },
  {
    id: 'governing-genai',
    category: 'Executive Brief',
    title: 'Governing generative AI: a one-page board paper',
    excerpt:
      'A board-ready briefing on how to authorize generative AI without committing to risk you do not understand.',
    author: 'Rachel Mendoza',
    role: 'Partner, Responsible AI',
    date: 'March 14, 2026',
    readTime: '10 min',
    image: '/images/insight-4.svg',
    tags: ['Governance', 'GenAI', 'Risk'],
    body: [
      'Boards are being asked to authorize generative AI investments they cannot fully evaluate. The right move is not to wait. The right move is to authorize bounded experiments under a clear risk framework.',
      'A one-page paper should cover: which use cases are pre-authorized, which require legal review, how data flows out of the enterprise, who owns evals, and what the kill switch is.',
      'When that paper exists, AI velocity goes up — not down. The teams know what is allowed. The board knows what is happening. Everybody sleeps better.',
    ],
  },
  {
    id: 'streaming-data',
    category: 'Field Guide',
    title: 'Real-time data streaming: when it pays off (and when it does not)',
    excerpt:
      'Streaming is not a religion. We map ten common workloads to streaming, micro-batch, and batch — with FinOps math.',
    author: 'James Cole',
    role: 'Principal Engineer, Streaming',
    date: 'February 27, 2026',
    readTime: '9 min',
    image: '/images/insight-5.svg',
    tags: ['Data Engineering', 'Streaming', 'FinOps'],
    body: [
      'Streaming is exciting and expensive. The right question is not "should we stream?" — it is "what is the cost of latency on this specific workload?"',
      'Across ten workloads we have studied, only four genuinely require streaming. Four more thrive on micro-batch. The remaining two perform best as straightforward nightly batches with proper SLAs.',
      'We share the decision framework, the FinOps math, and a sample architecture for each tier. The result is faster, cheaper, and easier to operate than treating streaming as the default answer.',
    ],
  },
  {
    id: 'vector-dbs',
    category: 'Benchmark',
    title: 'Vector databases in production: 2026 benchmark',
    excerpt:
      'Cost, recall, latency, hybrid search, and operational maturity across nine vector engines under realistic enterprise load.',
    author: 'Eden Navarro',
    role: 'Lead Researcher',
    date: 'February 11, 2026',
    readTime: '22 min',
    image: '/images/insight-6.svg',
    tags: ['GenAI', 'Vector DB', 'Benchmark'],
    body: [
      'We benchmarked nine vector databases against six realistic enterprise workloads: dense search, hybrid search, multi-tenant, schema-on-write, schema-on-read, and high-write streaming.',
      'No engine wins everything. Three engines lead at different operating points. We share the benchmark methodology, the workload definitions, and our recommendations by use case.',
      'The high-order takeaway: pick a vector engine based on your operations team\'s comfort, not raw recall. The recall gap closes in weeks; operational maturity takes years.',
    ],
  },
  {
    id: 'data-product-thinking',
    category: 'Perspective',
    title: 'Data product thinking: an org redesign, not a data redesign',
    excerpt:
      'Data mesh is mostly an organizational change. We share what works, what fails, and what to do in week one.',
    author: 'Priyanka Rao',
    role: 'Partner, Data Engineering',
    date: 'January 30, 2026',
    readTime: '11 min',
    image: '/images/insight-2.svg',
    tags: ['Data Mesh', 'Operating Model'],
    body: [
      'Data mesh succeeds or fails based on accountability, not architecture. The biggest predictor of success is whether business domains have an accountable data product manager — and a budget.',
      'The biggest predictor of failure is treating it as a platform initiative led by central IT. The platform is necessary but not sufficient. Without the org change, you end up with a data swamp run by a smaller central team.',
      'Our week-one playbook: identify three pioneer domains, name the product managers, define three data products with consumers and SLAs, and only then start building the platform.',
    ],
  },
  {
    id: 'rag-evals',
    category: 'Engineering',
    title: 'Evaluating RAG systems: faithfulness, recall, and the tests that catch regressions',
    excerpt:
      'Why most teams under-invest in RAG evals — and a practical eval harness that runs on every PR.',
    author: 'Dr. Anika Khoury',
    role: 'Global Head of AI Strategy',
    date: 'January 14, 2026',
    readTime: '13 min',
    image: '/images/insight-1.svg',
    tags: ['GenAI', 'RAG', 'Evaluation'],
    body: [
      'Your RAG system silently degraded yesterday. You will find out next month, when an executive asks why an answer is wrong. The cost will be a week of unscheduled work and the trust dividend you spent six months earning.',
      'A good RAG eval harness covers retrieval recall, generation faithfulness, refusal calibration, and end-to-end task accuracy. It runs on every PR. Failures gate deploys.',
      'We share our reference harness and the four trickiest evals to design well: refusal calibration, multi-hop reasoning, hallucination detection, and stylistic regression.',
    ],
  },
  {
    id: 'finops-data',
    category: 'Playbook',
    title: 'Data FinOps: cutting cloud spend without breaking adoption',
    excerpt:
      'Most cloud data bills are not too big — they are too unobservable. Here is how to fix that, without slowing teams down.',
    author: 'Marcus Okafor',
    role: 'Partner, Cloud & FinOps',
    date: 'January 03, 2026',
    readTime: '10 min',
    image: '/images/insight-3.svg',
    tags: ['FinOps', 'Cloud', 'Data Engineering'],
    body: [
      'In our last 38 platform engagements, average cost reduction was 52%. Almost none of it came from blunt instruments like reserved instances. Almost all of it came from observability — knowing exactly which query, model and team is spending what.',
      'A good Data FinOps program shows daily cost per data product, per query, per pipeline, per model — to the people running them. With the right dashboards, optimization is self-service. Engineers fix what they can see.',
      'We share the dashboards, the warehouse routing patterns, and the unit economics that make this work.',
    ],
  },
  {
    id: 'agents-enterprise',
    category: 'Engineering',
    title: 'Enterprise agents: tool use, memory, and the boring parts',
    excerpt:
      'Most enterprise agent demos fail in production. Here is what production agents actually need.',
    author: 'James Cole',
    role: 'Principal Engineer, Streaming',
    date: 'December 18, 2025',
    readTime: '12 min',
    image: '/images/insight-4.svg',
    tags: ['GenAI', 'Agents', 'Architecture'],
    body: [
      'Production-grade agents need three things demos rarely show: durable memory, idempotent tool calls, and a reliable approval and observability layer.',
      'When tool calls fail, agents need to retry idempotently — not double-charge a customer. When memory grows, it needs to be searchable, prunable, and explainable. When humans need to approve, they need a console — not a Slack DM.',
      'We share our reference architecture, including the patterns we use for memory and the pieces we have learned never to build ourselves.',
    ],
  },
  {
    id: 'ai-act',
    category: 'Executive Brief',
    title: 'The EU AI Act: what to do now, in plain English',
    excerpt:
      'A pragmatic, lawyer-reviewed playbook for compliance with the EU AI Act, mapped to engineering work.',
    author: 'Rachel Mendoza',
    role: 'Partner, Responsible AI',
    date: 'December 04, 2025',
    readTime: '15 min',
    image: '/images/insight-4.svg',
    tags: ['Regulation', 'EU AI Act', 'Governance'],
    body: [
      'The EU AI Act is now real. The good news: most enterprises that already do MLOps well have 80% of the engineering work done. The remaining 20% is documentation and process.',
      'We map every Act requirement to concrete engineering and process artifacts: risk classification, data governance evidence, model documentation, monitoring, incident reporting.',
      'Companies that start now will be ready. Companies that wait will spend much more later — and so will their lawyers.',
    ],
  },
  {
    id: 'data-platform-org',
    category: 'Perspective',
    title: 'Designing the data platform team: 6 archetypes that work',
    excerpt:
      'Six archetypes for the modern data platform team — and the staffing models we have seen succeed and fail.',
    author: 'Marcus Okafor',
    role: 'Partner, Cloud & FinOps',
    date: 'November 19, 2025',
    readTime: '10 min',
    image: '/images/insight-2.svg',
    tags: ['Operating Model', 'Talent'],
    body: [
      'The right data platform team depends on company size, data maturity, and the speed of business change. We have seen six archetypes work, and at least four ways to staff each.',
      'We share the archetypes, the headcount math, and the specific roles to hire first. We also share the failure modes — the staffing patterns that look efficient on paper but stall in practice.',
      'When in doubt, prefer a smaller, more senior team with a clear product mandate over a larger team with no mandate at all.',
    ],
  },
];

export const leadership = [
  {
    name: 'Qamar Shahbaz Ul Haq',
    role: 'Co-Founder & Chief Executive Officer',
    short: 'Visionary leader with deep expertise in building and delivering complex AI and data solutions at scale.',
    avatar: '/images/Qamar.jpeg',
    bio: 'As Co-Founder and CEO, he leads the company with a clear focus on execution, scalability, and long-term value creation. With a strong background in building and delivering complex solutions, he combines technical understanding with commercial discipline to turn vision into measurable outcomes. Known for his decisive leadership and hands-on approach, he drives the organisation\'s strategy, fosters a high-performance culture, and ensures the business consistently delivers for clients while expanding into new markets.',
  },
  {
    name: 'Rowan Vexley Grant',
    role: 'Co-Founder & Chief Commercial Officer, Europe & Africa',
    short: 'Over two decades of experience driving high-value commercial growth and delivering AI & Data projects across enterprise markets.',
    avatar: '/images/Rowan Vexley Grant.jpeg',
    bio: 'Rowan is the UK Head of Sales based in London, bringing over two decades of experience in driving high-value commercial growth and delivering AI, Data projects across enterprise markets. Known for his sharp strategic thinking and relentless execution, he has a proven track record of building strong client relationships, leading high-performing teams, and closing transformative deals that create long-term value. His energetic, results-focused approach ensures that ambitious ideas are translated into measurable outcomes, making him a key force behind the company\'s expansion in the UK and beyond.',
  },
  {
    name: 'Ethan Caldwell',
    role: 'Head of Sales, USA',
    short: 'Strong blend of commercial instinct and hands-on execution in complex, high-value enterprise engagements.',
    avatar: '/images/Ethan Caldwell.jpeg',
    bio: 'Ethan Caldwell leads sales operations across the United States, bringing a strong blend of commercial instinct and hands-on execution to complex, high-value engagements. With extensive experience delivering large-scale projects, he is known for cutting through noise, aligning stakeholders quickly, and driving deals to close with precision. His approachable style, combined with a sharp focus on outcomes, makes him highly effective in building trust with enterprise clients while consistently delivering measurable growth for the business.',
  },
];

export const milestones = [
  { year: 'Q2 2025', text: 'Dalytix formed around senior practitioners in enterprise data, analytics, AI and cloud delivery.' },
  { year: 'Q3 2025', text: 'Established the first delivery playbooks for strategy, lakehouse, GenAI and production AI engagements.' },
  { year: 'Q4 2025', text: 'Expanded the operating model for regulated, production-focused AI systems.' },
  { year: 'Q1 2026', text: 'Formalized the responsible AI, MLOps and data governance practice areas.' },
  { year: 'Q2 2026', text: 'Continued building reusable delivery assets for senior-led AI and data transformation work.' },
];

export const awards = [
  'Cloud-native architecture and data platform delivery',
  'Responsible AI governance and evaluation playbooks',
  'Lakehouse, warehouse and semantic-layer modernization',
  'Production GenAI, RAG and agentic workflow engineering',
  'MLOps controls for regulated and audit-heavy environments',
  'Senior-led delivery model from discovery through handover',
];

export const testimonials = [
  {
    quote:
      'Dalytix delivered the only AI program in our portfolio that hit every milestone, every quarter. Their craft is unmistakable.',
    author: 'Financial Crime Executive',
    role: 'Anonymized retail banking client',
  },
  {
    quote:
      'They earned the right to operate inside our hospitals. The clinical and engineering rigor are remarkable.',
    author: 'Clinical Technology Leader',
    role: 'Anonymized healthcare client',
  },
  {
    quote:
      'The first 90 days paid for the engagement. The next 365 paid for the next five years of strategy.',
    author: 'Chief Digital Officer',
    role: 'Anonymized retail client',
  },
  {
    quote:
      'We have worked with every name in this market. Dalytix is the team I would call again tomorrow.',
    author: 'Manufacturing Excellence Leader',
    role: 'Anonymized industrial client',
  },
];

export const events = [
  {
    date: 'May 14, 2026',
    name: 'Dalytix Data & AI Summit — London',
    type: 'Flagship event',
    location: 'London, UK · The Roundhouse',
    description: 'A focused executive session on production AI, operating models, and measurable delivery outcomes.',
  },
  {
    date: 'June 04, 2026',
    name: 'Production GenAI Roundtable — New York',
    type: 'Roundtable (CXO only)',
    location: 'New York, USA · Dalytix NYC',
    description: 'Off-the-record conversation for enterprise leaders and the Dalytix Responsible AI team.',
  },
  {
    date: 'June 19, 2026',
    name: 'Lakehouse Bootcamp — Remote',
    type: 'Workshop',
    location: 'Remote · Online',
    description: 'Two-day, hands-on lakehouse architecture bootcamp for senior data engineers.',
  },
  {
    date: 'July 09, 2026',
    name: 'Responsible AI for Boards — Online',
    type: 'Executive briefing',
    location: 'Remote · Online',
    description: 'A boardroom briefing on the EU AI Act, evaluation, and audit-ready governance.',
  },
];

export const careers = [
  {
    role: 'Principal AI Engineer (GenAI)',
    location: 'London · UK or Remote',
    type: 'Full-time',
    description: 'Lead architecture for enterprise GenAI builds — RAG, agents, evals, and production-grade serving.',
  },
  {
    role: 'Senior Data Engineer (Lakehouse)',
    location: 'London · UK',
    type: 'Full-time',
    description: 'Design and build domain-aligned data products on Databricks and Snowflake.',
  },
  {
    role: 'MLOps Tech Lead',
    location: 'New York · USA',
    type: 'Full-time',
    description: 'Own the production AI release process for clients in financial services and healthcare.',
  },
  {
    role: 'Founding Customer Success Lead',
    location: 'London · UK',
    type: 'Full-time',
    description: 'Own client outcomes and expansion across our first wave of long-term clients.',
  },
  {
    role: 'Cloud & FinOps Architect',
    location: 'Remote · EMEA',
    type: 'Full-time',
    description: 'Lead lakehouse modernization and cloud FinOps engagements across EMEA.',
  },
  {
    role: 'Senior Frontend Engineer (Data Apps)',
    location: 'Remote · Global',
    type: 'Full-time',
    description: 'Build internal data products and AI consoles used by our delivery teams.',
  },
];

export const offices = [
  { city: 'Altrincham', country: 'UK', region: 'Headquarters — WA14 2YQ', image: '/images/office-london.svg' },
  { city: 'New York', country: 'USA', region: 'Americas Hub', image: '/images/office-newyork.svg' },
  { city: 'Remote', country: 'Global', region: 'Distributed Team', image: '/images/office-singapore.svg' },
];

export const newsStrip = [
  { date: 'Apr 28, 2026', headline: 'Dalytix publishes production AI operating model briefing' },
  { date: 'Apr 15, 2026', headline: 'New responsible AI evaluation checklist released' },
  { date: 'Apr 02, 2026', headline: 'Anonymized GenAI case study added to the Dalytix library' },
  { date: 'Mar 18, 2026', headline: 'Lakehouse modernization playbook updated for 2026' },
  { date: 'Mar 04, 2026', headline: 'Clinical AI governance briefing now available' },
];

export const principles = [
  {
    title: 'Production over prototype',
    text: 'We measure success in deployed systems, not slide decks. Production rigor is a design choice from week one.',
  },
  {
    title: 'Outcomes over outputs',
    text: 'Every engagement is anchored to a measurable business outcome — and the leadership team that owns it.',
  },
  {
    title: 'Trust by construction',
    text: 'Security, governance, lineage and observability are not retrofits. They are designed into the architecture.',
  },
  {
    title: 'Senior delivery, always',
    text: 'No bait-and-switch. The senior engineers in the proposal are the senior engineers in the delivery.',
  },
  {
    title: 'Open by default',
    text: 'We bias to open standards, open formats, and clean exits. We make ourselves replaceable on purpose.',
  },
  {
    title: 'Honest economics',
    text: 'We bring FinOps thinking from day one. The TCO of every choice is visible and tracked.',
  },
];

export const process = [
  {
    step: '01',
    title: 'Discover',
    text: 'Anchor the engagement to a measurable outcome. Map data, technology and organizational realities.',
  },
  {
    step: '02',
    title: 'Architect',
    text: 'Design the operating model, target architecture, and a delivery plan with clear gates.',
  },
  {
    step: '03',
    title: 'Build',
    text: 'Deliver in compounding increments. Every release is production-grade and reviewable.',
  },
  {
    step: '04',
    title: 'Deploy',
    text: 'Roll out with observability, governance, runbooks, and a real on-call experience.',
  },
  {
    step: '05',
    title: 'Operate',
    text: 'Run in production with SLAs, evaluations, and continuous improvement until handover.',
  },
  {
    step: '06',
    title: 'Hand over',
    text: 'Transfer ownership cleanly. Your team is the long-term steward — by design.',
  },
];
